import React, { ReactNode } from 'react';
import { TrendingUp, Target, Calendar, AlertTriangle } from 'lucide-react';
import BaseTrajectoryCard, { 
  BaseTrajectoryProps, 
  ThemeVocabulary, 
  TrajectoryCardRenderer 
} from './BaseTrajectoryCard';

// Default theme trajectory card implementation
class DefaultTrajectoryCardImplementation extends BaseTrajectoryCard {
  
  getThemeVocabulary(): ThemeVocabulary {
    return {
      unit: 'points',
      pace: 'points/day',
      goal: 'Nintendo Switch',
      completion: 'completed',
      current: 'Current Pace',
      remaining: 'Remaining'
    };
  }
  
  getContainerClassName(): string {
    return "bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20";
  }
  
  renderHeader(): ReactNode {
    const status = this.determineStatus();
    const statusConfig = {
      excellent: { bg: 'bg-green-500/20', text: 'text-green-300', border: 'border-green-500/30', label: '✅ On Track' },
      good: { bg: 'bg-green-500/20', text: 'text-green-300', border: 'border-green-500/30', label: '✅ On Track' },
      monitor: { bg: 'bg-yellow-500/20', text: 'text-yellow-300', border: 'border-yellow-500/30', label: '⚠️ Monitor' },
      speedup: { bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-500/30', label: '🚨 Speed Up!' }
    };
    
    const config = statusConfig[status];
    
    return (
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-white" />
          <h3 className="text-lg font-semibold text-white">Goal Trajectory</h3>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text} border ${config.border}`}>
          {config.label}
        </div>
      </div>
    );
  }
  
  renderPredictionSection(): ReactNode {
    const projectedDate = this.calculateProjectedDate();
    const remaining = this.calculateRemaining();
    const vocabulary = this.getThemeVocabulary();
    
    return (
      <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg p-4 border border-blue-500/30 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-4 h-4 text-blue-300" />
          <div className="text-blue-300 text-sm font-medium">At your current pace...</div>
        </div>
        <div className="text-white text-lg font-bold mb-1">
          {vocabulary.goal} in {this.formatTimeRemaining(projectedDate)}
        </div>
        <div className="text-white/70 text-xs">
          Earning {this.props.dailyAverage.toFixed(1)} {vocabulary.unit}/day • {remaining} {vocabulary.unit} to go
        </div>
      </div>
    );
  }
  
  renderProgressVisualization(): ReactNode {
    const progressPercentage = this.calculateProgressPercentage();
    const required = this.calculateRequiredDailyPace();
    
    // Generate trajectory chart data points
    const generateTrajectoryData = () => {
      const days = 30; // Show 30 days trajectory
      const currentTrajectory = [];
      const optimizedTrajectory = [];
      
      for (let i = 0; i <= days; i++) {
        const currentProjection = Math.min(
          this.props.currentPoints + (this.props.dailyAverage * i),
          this.props.targetPoints
        );
        const optimizedProjection = Math.min(
          this.props.currentPoints + (required * i),
          this.props.targetPoints
        );
        
        currentTrajectory.push({
          day: i,
          points: currentProjection,
          percentage: (currentProjection / this.props.targetPoints) * 100
        });
        
        optimizedTrajectory.push({
          day: i,
          points: optimizedProjection,
          percentage: (optimizedProjection / this.props.targetPoints) * 100
        });
      }
      
      return { currentTrajectory, optimizedTrajectory };
    };
    
    const { currentTrajectory, optimizedTrajectory } = generateTrajectoryData();
    
    return (
      <div className="mb-4">
        <div className="flex justify-between text-xs text-white/60 mb-2">
          <span>Progress Trajectory (30 days)</span>
          <span>{this.props.targetPoints} pts goal</span>
        </div>
        
        <div className="relative h-24 bg-black/20 rounded-lg p-2">
          <svg viewBox="0 0 300 80" className="w-full h-full">
            {/* Grid lines */}
            {[25, 50, 75, 100].map(percent => (
              <line
                key={percent}
                x1="0"
                y1={80 - (percent * 0.8)}
                x2="300"
                y2={80 - (percent * 0.8)}
                stroke="rgba(255,255,255,0.1)"
                strokeWidth="0.5"
              />
            ))}
            
            {/* Current trajectory line */}
            <polyline
              points={currentTrajectory.map((point, i) => 
                `${(i / 30) * 300},${80 - (point.percentage * 0.8)}`
              ).join(' ')}
              fill="none"
              stroke="#ef4444"
              strokeWidth="2"
              strokeDasharray="3,3"
            />
            
            {/* Optimized trajectory line */}
            <polyline
              points={optimizedTrajectory.map((point, i) => 
                `${(i / 30) * 300},${80 - (point.percentage * 0.8)}`
              ).join(' ')}
              fill="none"
              stroke="#22c55e"
              strokeWidth="2"
            />
            
            {/* Current position marker */}
            <circle
              cx="0"
              cy={80 - (progressPercentage * 0.8)}
              r="3"
              fill="#fbbf24"
              stroke="#ffffff"
              strokeWidth="1"
            />
          </svg>
        </div>
        
        <div className="flex justify-between text-xs mt-2">
          <div className="flex items-center gap-2">
            <div className="w-3 h-0.5 bg-red-400 opacity-60" style={{backgroundImage: 'repeating-linear-gradient(to right, transparent, transparent 2px, #ef4444 2px, #ef4444 4px)'}}></div>
            <span className="text-white/60">Current pace</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-0.5 bg-green-400"></div>
            <span className="text-white/60">Recommended pace</span>
          </div>
        </div>
      </div>
    );
  }
  
  renderMetrics(): ReactNode {
    const daysToComplete = this.calculateDaysToComplete();
    const required = this.calculateRequiredDailyPace();
    const vocabulary = this.getThemeVocabulary();
    const daysUntilTarget = this.props.targetDate ? 
      Math.ceil((new Date(this.props.targetDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : null;
    
    return (
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white/5 rounded-lg p-3">
          <div className="text-white/60 text-xs">{vocabulary.current}</div>
          <div className="text-white font-semibold">{this.props.dailyAverage.toFixed(1)} {vocabulary.pace}</div>
          <div className="text-white/50 text-xs">
            {daysToComplete} days to goal
          </div>
        </div>
        
        <div className="bg-white/5 rounded-lg p-3">
          <div className="text-white/60 text-xs">Recommended Pace</div>
          <div className="text-white font-semibold">{required.toFixed(1)} {vocabulary.pace}</div>
          <div className="text-white/50 text-xs">
            {daysUntilTarget ? `${daysUntilTarget} days left` : 'No target set'}
          </div>
        </div>
      </div>
    );
  }
  
  renderActionableInsights(): ReactNode {
    const statusMessage = this.getStatusMessage();
    const status = this.determineStatus();
    const isOnTrack = status === 'excellent' || status === 'good';
    
    return (
      <div>
        <div className="bg-white/5 rounded-lg p-3 mb-3">
          <div className="flex items-start gap-2">
            {isOnTrack ? (
              <Target className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
            )}
            <div>
              <div className="text-white text-sm font-medium mb-1">
                {statusMessage.title}
              </div>
              <div className="text-white/70 text-xs">
                {statusMessage.description}
              </div>
            </div>
          </div>
        </div>
        
        {statusMessage.suggestions.length > 0 && (
          <div className="pt-3 border-t border-white/10">
            <div className="text-xs text-white/60 mb-2">💡 Quick boost ideas:</div>
            <div className="flex flex-wrap gap-1">
              {statusMessage.suggestions.map((suggestion, i) => (
                <span key={i} className="text-xs bg-white/10 text-white/80 px-2 py-1 rounded">
                  {suggestion}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
}

// React component that creates and uses the Default implementation
export const DefaultTrajectoryCard: React.FC<BaseTrajectoryProps> = (props) => {
  const implementation = new DefaultTrajectoryCardImplementation(props);
  return <TrajectoryCardRenderer {...props} implementation={implementation} />;
};